import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSpeciesList } from "@/hooks";
import { api, campaignApi } from "@/lib/api";
import { computeFileSha256 } from "@/lib/fileChecksum";
import { UTM_LABEL, fromUtm22S, isPlausibleUtm22S, toUtm22S } from "@/lib/utm";
import type {
  AreaPilotoApi,
  CampaignSummaryApi,
  GeocodeResultApi,
  NearbyTreeApi,
  PointTerritorialContextApi,
  ProjectApi,
  QuadraApi,
  TreeCreatePayload,
} from "@/lib/api";
import type { FeatureCollection, GeoJsonObject } from "geojson";
import TreeMapPicker, { type Coordinates } from "./components/TreeMapPicker";
import NearbyTreeList from "./components/NearbyTreeList";
import GpsLocator, { type GpsFix } from "./components/GpsLocator";
import DerivedContextPanel from "./components/DerivedContextPanel";
import AddressSearch from "./components/AddressSearch";
import BasePhotoPicker from "./components/BasePhotoPicker";

const STEPS = [
  { label: "Localização", icon: "ri-map-pin-line" },
  { label: "Identificação", icon: "ri-leaf-line" },
  { label: "Confirmação", icon: "ri-check-double-line" },
];

const INDIVIDUAL_TYPES = [
  { value: "ARVORE", label: "Árvore" },
  { value: "PALMEIRA", label: "Palmeira" },
  { value: "MUDA", label: "Muda" },
  { value: "ARBUSTO_PORTE_ARBOREO", label: "Arbusto de porte arbóreo" },
];

/**
 * How the operator informed the position. This is recorded with the tree
 * (migration 054) because a point clicked over an outdated orthophoto is not
 * worth the same as one confirmed on site, and without it the two are
 * indistinguishable afterwards.
 */
type LocationMethod = "map" | "coordinates" | "address" | "gps";

/**
 * Where a coordinate came from, in the vocabulary the database already accepts
 * (migration 054). GEOCODING is deliberately distinct from WEBGIS_CLICK: a
 * position proposed from an address and then adjusted is not the same evidence
 * as one pointed at directly on the orthophoto.
 */
const POSITIONING_METHOD: Record<LocationMethod, string> = {
  map: "WEBGIS_CLICK",
  coordinates: "MANUAL_COORDINATES",
  address: "GEOCODING",
  gps: "DEVICE_GPS",
};

const REGISTRATION_SOURCE: Record<LocationMethod, string> = {
  map: "WEBGIS_MAP",
  coordinates: "MANUAL_COORDINATES",
  address: "DIRECT_FORM",
  gps: "DEVICE_GPS",
};

const DEFAULT_RADIUS_M = 15;

// Pilot area with real cadastral base (44 blocks, 600 lots, 116 streets).
// The map opens here because this is where a coordinate actually resolves into
// neighbourhood, block, lot and street — elsewhere the derivation comes back
// empty and the operator has to type everything by hand.
const BASE_STUDY_AREA_CODE = "SBV-REAL-001";

interface FiqForm {
  project_id: number | null;
  campaign_id: number | null;
  species_id: number | null;
  individual_type: string;
  cta_code: string;
  neighborhood: string;
  street: string;
  address_reference: string;
  field_notes: string;
  location_source: string;
}

const EMPTY_FORM: FiqForm = {
  project_id: null,
  campaign_id: null,
  species_id: null,
  individual_type: "ARVORE",
  cta_code: "",
  neighborhood: "",
  street: "",
  address_reference: "",
  field_notes: "",
  location_source: "",
};

function formatCoord(value: number): string {
  return value.toFixed(7).replace(".", ",");
}

export default function NovoExemplarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [method, setMethod] = useState<LocationMethod>("map");
  const [coords, setCoords] = useState<Coordinates | null>(null);
  // Incerteza informada pelo aparelho; só vale enquanto o marcador está onde o GPS o pôs.
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [latInput, setLatInput] = useState("");
  const [lonInput, setLonInput] = useState("");
  // Typed coordinates in degrees (WGS 84) or in UTM 22S (SIRGAS 2000), the
  // system the municipality works in.  The point is stored in degrees.
  const [coordSystem, setCoordSystem] = useState<"graus" | "utm">("graus");
  const [eInput, setEInput] = useState("");
  const [nInput, setNInput] = useState("");
  const [form, setForm] = useState<FiqForm>(EMPTY_FORM);

  const [projects, setProjects] = useState<ProjectApi[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummaryApi[]>([]);
  const { items: species } = useSpeciesList();

  const [studyArea, setStudyArea] = useState<AreaPilotoApi | null>(null);
  const [blocks, setBlocks] = useState<QuadraApi[]>([]);
  const [context, setContext] = useState<PointTerritorialContextApi | null>(null);

  const [nearby, setNearby] = useState<NearbyTreeApi[]>([]);
  const [checkingNearby, setCheckingNearby] = useState(false);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);

  const [photo, setPhoto] = useState<File | null>(null);
  const [addressPick, setAddressPick] = useState<GeocodeResultApi | null>(null);

  const [saving, setSaving] = useState(false);
  const [savingStep, setSavingStep] = useState<string | null>(null);
  // Set once the tree exists. From then on the primary action can only open the
  // record — pressing "Cadastrar" again would create a duplicate of a tree that
  // was already saved.
  const [createdTreeId, setCreatedTreeId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // --- Projects -------------------------------------------------------------
  // Consumed raw instead of through useProjectsList(): that hook's mapper
  // exposes project_uuid as the id, and POST /trees needs the numeric one.
  useEffect(() => {
    let active = true;
    api
      .projects()
      .then((rows) => {
        if (!active) return;
        setProjects(rows);
        const fromQuery = Number(searchParams.get("project_id"));
        const preset = rows.find((p) => p.project_id === fromQuery);
        const only = rows.length === 1 ? rows[0] : undefined;
        const chosen = preset ?? only;
        if (chosen) {
          setForm((f) => ({ ...f, project_id: chosen.project_id }));
        }
      })
      .catch(() => {
        if (active) setProjects([]);
      });
    return () => {
      active = false;
    };
  }, [searchParams]);

  // --- Coordinate handed over by another screen -----------------------------
  // The WebGIS console (Fluxo B, §5) positions the point on its own map and
  // sends the operator here, so the registration itself has a single
  // implementation instead of a second, divergent one inside the console.
  const preloadedRef = useRef(false);
  useEffect(() => {
    if (preloadedRef.current) return;
    const rawLat = searchParams.get("lat");
    const rawLon = searchParams.get("lng") ?? searchParams.get("lon");
    // Number(null) and Number("") are both 0, which is a valid coordinate in
    // the Atlantic — the absence of the parameter has to be checked first.
    if (!rawLat?.trim() || !rawLon?.trim()) return;
    const lat = Number(rawLat);
    const lon = Number(rawLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return;
    preloadedRef.current = true;
    setCoords({ latitude: lat, longitude: lon });
    setLatInput(String(lat));
    setLonInput(String(lon));
    // Arriving from the console is a map click, just made on the other map.
    setMethod("map");
  }, [searchParams]);

  // --- Study area -----------------------------------------------------------
  useEffect(() => {
    let active = true;
    api
      .areasPiloto()
      .then((rows) => {
        if (!active) return;
        const base = rows.find((a) => a.code === BASE_STUDY_AREA_CODE) ?? null;
        setStudyArea(base);
        if (base) {
          return api.quadras(base.id).then((qs) => active && setBlocks(qs));
        }
      })
      .catch(() => active && setStudyArea(null));
    return () => {
      active = false;
    };
  }, []);

  const studyAreaGeo = useMemo<GeoJsonObject | null>(
    () => (studyArea?.geom as GeoJsonObject | undefined) ?? null,
    [studyArea],
  );

  const blocksGeo = useMemo<FeatureCollection | null>(() => {
    const features = blocks
      .filter((q) => q.geom)
      .map((q) => ({
        type: "Feature" as const,
        geometry: q.geom as GeoJsonObject as never,
        properties: { code: q.code, name: q.name },
      }));
    return features.length ? { type: "FeatureCollection", features } : null;
  }, [blocks]);

  useEffect(() => {
    if (!form.project_id) {
      setCampaigns([]);
      return;
    }
    let active = true;
    campaignApi
      .list({ project_id: form.project_id, status: "ATIVA" })
      .then((rows) => active && setCampaigns(rows))
      .catch(() => active && setCampaigns([]));
    return () => {
      active = false;
    };
  }, [form.project_id]);

  // --- Duplicate check ------------------------------------------------------
  // Debounced: dragging the marker fires continuously and each move would
  // otherwise be a request.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!coords) {
      setNearby([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCheckingNearby(true);
    setDuplicateAcknowledged(false);

    debounceRef.current = setTimeout(() => {
      const seq = ++requestSeq.current;
      // Duplicate check and territorial derivation answer the same question —
      // "what is already here?" — so they ride the same debounce.
      Promise.allSettled([
        campaignApi.nearbyTrees({
          latitude: coords.latitude,
          longitude: coords.longitude,
          radius_m: DEFAULT_RADIUS_M,
        }),
        campaignApi.territorialContext({
          latitude: coords.latitude,
          longitude: coords.longitude,
          // The lot suggestion widens its tolerance with the GPS error.
          accuracy_m: method === "gps" && gpsAccuracy != null ? gpsAccuracy : undefined,
        }),
      ]).then(([nearbyResult, contextResult]) => {
        // A slower earlier request must not overwrite a newer result.
        if (seq !== requestSeq.current) return;
        setNearby(nearbyResult.status === "fulfilled" ? nearbyResult.value : []);
        setContext(contextResult.status === "fulfilled" ? contextResult.value : null);
        setCheckingNearby(false);
      });
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [coords, method, gpsAccuracy]);

  // Pre-fills the address from what the geometry answered, never overwriting
  // something the operator typed: a typed value is their correction.
  useEffect(() => {
    if (!context) return;
    setForm((f) => ({
      ...f,
      neighborhood: f.neighborhood || context.neighborhood || "",
      street: f.street || context.street || "",
    }));
  }, [context]);

  const handleAddressPick = useCallback((result: GeocodeResultApi) => {
    setAddressPick(result);
    setCoords({ latitude: result.latitude, longitude: result.longitude });
    setLatInput(String(result.latitude));
    setLonInput(String(result.longitude));
    setError(null);
    // The chosen address is closer to a typed value than to a derivation — the
    // operator picked it — so it fills the form directly. It still only fills
    // blanks, and the derivation that runs next will not overwrite it.
    //
    // This also covers a real gap: a point that lands exactly on a street at
    // the edge of the neighbourhood is not ST_Contains'd by it, so the
    // derivation comes back without a bairro even though the search knew one.
    setForm((f) => ({
      ...f,
      street: f.street || result.street || (result.kind === "LOGRADOURO" ? result.label : ""),
      neighborhood: f.neighborhood || result.neighborhood || "",
    }));
  }, []);

  const handleGpsFix = useCallback((fix: GpsFix) => {
    setCoords({ latitude: fix.latitude, longitude: fix.longitude });
    setLatInput(String(fix.latitude));
    setLonInput(String(fix.longitude));
    setGpsAccuracy(fix.accuracy);
    setAddressPick(null);
    setError(null);
  }, []);

  const handleMapChange = useCallback((next: Coordinates) => {
    // Arrastar o marcador depois de um GPS é um ajuste manual: a precisão do
    // aparelho não descreve mais o novo ponto, então o método volta a ser o clique.
    setMethod((m) => (m === "gps" ? "map" : m));
    setGpsAccuracy(null);
    setCoords(next);
    setLatInput(String(next.latitude));
    setLonInput(String(next.longitude));
  }, []);

  // Any way of placing the point keeps the UTM fields in step with it.
  useEffect(() => {
    if (!coords) return;
    const u = toUtm22S(coords.latitude, coords.longitude);
    setEInput(u.e.toFixed(2));
    setNInput(u.n.toFixed(2));
  }, [coords]);

  function applyTypedCoordinates() {
    if (coordSystem === "utm") {
      // Accepts "685499,27", "685.499,27" and "685499.27".
      const parse = (v: string) => Number(v.includes(",") ? v.replace(/\./g, "").replace(",", ".") : v);
      const e = parse(eInput.trim());
      const n = parse(nInput.trim());
      if (!Number.isFinite(e) || !Number.isFinite(n) || !isPlausibleUtm22S(e, n)) {
        setError("Coordenada UTM inválida para a zona 22S. Informe E (leste, ~6 dígitos) e N (norte, ~7 dígitos) em metros.");
        return;
      }
      const ll = fromUtm22S(e, n);
      setError(null);
      setLatInput(ll.latitude.toFixed(7));
      setLonInput(ll.longitude.toFixed(7));
      setCoords({ latitude: Number(ll.latitude.toFixed(7)), longitude: Number(ll.longitude.toFixed(7)) });
      return;
    }
    const lat = Number(latInput.replace(",", "."));
    const lon = Number(lonInput.replace(",", "."));
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      setError("Latitude inválida. Use um valor entre -90 e 90.");
      return;
    }
    if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
      setError("Longitude inválida. Use um valor entre -180 e 180.");
      return;
    }
    setError(null);
    setCoords({ latitude: lat, longitude: lon });
  }

  const selectedProject = useMemo(
    () => projects.find((p) => p.project_id === form.project_id),
    [projects, form.project_id],
  );
  const selectedSpecies = useMemo(
    () => species.find((s) => Number(s.id) === form.species_id),
    [species, form.species_id],
  );

  const blockingDuplicate = nearby.length > 0 && !duplicateAcknowledged;
  const canLeaveLocation = coords !== null && !checkingNearby && !blockingDuplicate;
  const canLeaveIdentification = form.project_id !== null;

  async function handleSubmit() {
    if (!coords || !form.project_id) return;
    setSaving(true);
    setError(null);
    setWarning(null);
    setSavingStep("Cadastrando exemplar...");

    const payload: TreeCreatePayload = {
      project_id: form.project_id,
      latitude: coords.latitude,
      longitude: coords.longitude,
      individual_type: form.individual_type,
      species_id: form.species_id,
      campaign_id: form.campaign_id,
      // Left empty on purpose when the operator did not type one: the server
      // assigns the code so the identity does not depend on the frontend.
      cta_code: form.cta_code.trim() || undefined,
      neighborhood: form.neighborhood.trim() || null,
      street: form.street.trim() || null,
      address_reference: form.address_reference.trim() || null,
      field_notes: form.field_notes.trim() || null,
      registration_source: REGISTRATION_SOURCE[method],
      positioning_method: POSITIONING_METHOD[method],
      location_source: form.location_source.trim() || null,
      registration_confidence: form.species_id ? "MEDIUM" : "UNIDENTIFIED",
      location_accuracy_m: method === "gps" && gpsAccuracy != null ? gpsAccuracy : undefined,
      // Confirmed only when the device itself was at the tree (GPS fix with a
      // declared accuracy). Clicks, typed coordinates and addresses are office
      // work and stay unconfirmed.
      field_confirmed: method === "gps" && gpsAccuracy != null,
      // The operator saw the neighbours and said they are different trees: the
      // server must not reuse one of them in place of that decision.
      acknowledged_neighbor_ids: duplicateAcknowledged ? nearby.map((n) => n.id) : undefined,
    };

    let created;
    try {
      created = await campaignApi.createTree(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao cadastrar exemplar");
      setSaving(false);
      setSavingStep(null);
      return;
    }

    if (!created.is_new && created.duplicate_of) {
      setError(
        `O servidor identificou um exemplar existente a menos de 3 m (${created.cta_code}). ` +
          "Nenhum cadastro novo foi criado.",
      );
      setSaving(false);
      setSavingStep(null);
      return;
    }

    // The tree exists from here on. A photo that fails to upload must not undo
    // that or look like a failed registration — it is reported as a pending
    // photo on a tree that was created.
    if (photo) {
      setSavingStep("Enviando foto...");
      try {
        const checksum = await computeFileSha256(photo);
        await campaignApi.uploadTreePhoto(created.id, photo, checksum ?? undefined);
      } catch (err) {
        const reason = err instanceof Error ? err.message : "falha no envio";
        setCreatedTreeId(created.id);
        setWarning(
          `Exemplar ${created.cta_code} foi cadastrado, mas a foto não subiu (${reason}). ` +
            "O cadastro está salvo — anexe a foto pela ficha do exemplar.",
        );
        setSaving(false);
        setSavingStep(null);
        return;
      }
    }

    navigate(`/cta/${created.id}`);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/cta")}
          className="w-8 h-8 rounded-lg border border-background-200 flex items-center justify-center text-foreground-500 hover:bg-background-50 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading">
            Novo Exemplar Arbóreo
          </h1>
          <p className="text-xs text-foreground-500">
            Cadastro base FIQ-1 — não exige vistoria técnica
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors w-full cursor-pointer ${
                i === step
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : i < step
                    ? "bg-success-50 text-success-700 border border-success-200"
                    : "bg-background-50 text-foreground-400 border border-background-200"
              }`}
            >
              <i className={`${i < step ? "ri-check-line" : s.icon} text-sm`}></i>
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div
                className={`w-4 h-0.5 flex-shrink-0 ${i < step ? "bg-success-300" : "bg-background-200"}`}
              />
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-danger-50 border border-danger-200 px-3 py-2.5">
          <i className="ri-error-warning-line text-danger-500 mt-0.5" />
          <p className="text-sm text-danger-700">{error}</p>
        </div>
      )}

      {warning && (
        <div className="flex items-start gap-2 rounded-lg bg-warning-50 border border-warning-200 px-3 py-2.5">
          <i className="ri-alert-line text-warning-600 mt-0.5" />
          <p className="text-sm text-warning-800">{warning}</p>
        </div>
      )}

      <div className="bg-white rounded-card border border-background-200/70 p-4 sm:p-6 space-y-5">
        {/* ---------------------------------------------------------------- */}
        {step === 0 && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {(
                [
                  { value: "gps", label: "Usar GPS", icon: "ri-gps-line" },
                  { value: "map", label: "Selecionar no mapa", icon: "ri-map-2-line" },
                  { value: "coordinates", label: "Informar coordenadas", icon: "ri-focus-3-line" },
                  { value: "address", label: "Informar endereço", icon: "ri-search-line" },
                ] as Array<{ value: LocationMethod; label: string; icon: string }>
              ).map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setMethod(option.value);
                    // A precisão pertence à leitura de GPS; outro método não a herda.
                    if (option.value !== "gps") setGpsAccuracy(null);
                  }}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border text-center transition-colors cursor-pointer ${
                    method === option.value
                      ? "bg-primary-50 text-primary-700 border-primary-200"
                      : "bg-background-50 text-foreground-500 border-background-200 hover:bg-background-100"
                  }`}
                >
                  <i className={option.icon} />
                  {option.label}
                </button>
              ))}
            </div>

            {method === "coordinates" && (
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Sistema de coordenadas">
                {([
                  ["graus", "Graus (WGS 84)"],
                  ["utm", "UTM 22S (SIRGAS 2000)"],
                ] as const).map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    role="radio"
                    aria-checked={coordSystem === code}
                    onClick={() => setCoordSystem(code)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer ${
                      coordSystem === code
                        ? "border-primary-300 bg-primary-50 text-primary-800"
                        : "border-background-200 text-foreground-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {method === "coordinates" && (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                {coordSystem === "graus" ? (
                  <>
                    <label className="block">
                      <span className="text-xs font-medium text-foreground-700">Latitude</span>
                      <input
                        value={latInput}
                        onChange={(e) => setLatInput(e.target.value)}
                        placeholder="-16,7145"
                        inputMode="decimal"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-mono"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-foreground-700">Longitude</span>
                      <input
                        value={lonInput}
                        onChange={(e) => setLonInput(e.target.value)}
                        placeholder="-49,2620"
                        inputMode="decimal"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-mono"
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <label className="block">
                      <span className="text-xs font-medium text-foreground-700">E — leste (m)</span>
                      <input
                        value={eInput}
                        onChange={(e) => setEInput(e.target.value)}
                        placeholder="685499,27"
                        inputMode="decimal"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-mono"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-foreground-700">N — norte (m)</span>
                      <input
                        value={nInput}
                        onChange={(e) => setNInput(e.target.value)}
                        placeholder="8150586,91"
                        inputMode="decimal"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-mono"
                      />
                    </label>
                  </>
                )}
                <button
                  onClick={applyTypedCoordinates}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium cursor-pointer hover:bg-primary-700"
                >
                  Posicionar
                </button>
                {coordSystem === "utm" && (
                  <p className="sm:col-span-3 text-[11px] text-foreground-500">
                    {UTM_LABEL}. O ponto é gravado em graus (EPSG:4326); a conversão é feita aqui.
                  </p>
                )}
              </div>
            )}

            {method === "address" && <AddressSearch onPick={handleAddressPick} />}

            {method === "gps" && <GpsLocator onFix={handleGpsFix} accuracy={gpsAccuracy} />}

            {addressPick && coords && (
              <div className="flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-200 px-3 py-2.5">
                <i className="ri-map-pin-user-line text-primary-600 mt-0.5" />
                <p className="text-xs text-primary-800">
                  Posição proposta a partir de{" "}
                  <strong>{addressPick.label}</strong>. Ajuste o marcador até o
                  tronco antes de continuar.
                </p>
              </div>
            )}

            {studyArea && (
              <p className="text-xs text-foreground-500">
                Base cadastral: <strong className="text-foreground-700">{studyArea.name}</strong>
                {blocks.length > 0 && ` — ${blocks.length} quadras mapeadas`}
              </p>
            )}

            <TreeMapPicker
              value={coords}
              onChange={handleMapChange}
              nearby={nearby}
              studyArea={studyAreaGeo}
              blocks={blocksGeo}
            />

            {coords && (
              <div className="flex items-center gap-2 text-xs font-mono text-foreground-500">
                <i className="ri-map-pin-2-line" />
                {formatCoord(coords.latitude)} , {formatCoord(coords.longitude)}
              </div>
            )}

            {coords && <DerivedContextPanel context={context} loading={checkingNearby} />}

            {coords && (
              <NearbyTreeList
                trees={nearby}
                loading={checkingNearby}
                radiusM={DEFAULT_RADIUS_M}
                onOpenTree={(id) => window.open(`/cta/${id}`, "_blank")}
              />
            )}

            {nearby.length > 0 && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={duplicateAcknowledged}
                  onChange={(e) => setDuplicateAcknowledged(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm text-foreground-700">
                  Verifiquei os exemplares próximos e confirmo que este é um{" "}
                  <strong>indivíduo novo</strong>.
                </span>
              </label>
            )}
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 1 && (
          <>
            <label className="block">
              <span className="text-xs font-medium text-foreground-700">
                Projeto <span className="text-danger-500">*</span>
              </span>
              <select
                value={form.project_id ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    project_id: e.target.value ? Number(e.target.value) : null,
                    campaign_id: null,
                  })
                }
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
              >
                <option value="">Selecione o projeto</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">Tipo de indivíduo</span>
                <select
                  value={form.individual_type}
                  onChange={(e) => setForm({ ...form, individual_type: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
                >
                  {INDIVIDUAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-foreground-700">Campanha</span>
                <select
                  value={form.campaign_id ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, campaign_id: e.target.value ? Number(e.target.value) : null })
                  }
                  disabled={campaigns.length === 0}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm disabled:bg-background-50 disabled:text-foreground-400"
                >
                  <option value="">
                    {campaigns.length === 0 ? "Nenhuma campanha ativa" : "Sem campanha"}
                  </option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">Espécie</span>
              <select
                value={form.species_id ?? ""}
                onChange={(e) =>
                  setForm({ ...form, species_id: e.target.value ? Number(e.target.value) : null })
                }
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
              >
                {/* A tree may exist without botanical identification: the
                    operator must not be forced to guess the species. */}
                <option value="">Não identificada</option>
                {species.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.popularName} — {s.scientificName}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-foreground-400 mt-1 block">
                Pode ficar sem identificação e ser definida depois, na vistoria.
              </span>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">Código CTA</span>
              <input
                value={form.cta_code}
                onChange={(e) => setForm({ ...form, cta_code: e.target.value })}
                placeholder="Deixe em branco para o sistema gerar"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm font-mono"
              />
              <span className="text-[11px] text-foreground-400 mt-1 block">
                Em branco, o servidor atribui o próximo código livre do projeto.
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">Logradouro</span>
                <input
                  value={form.street}
                  onChange={(e) => setForm({ ...form, street: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-foreground-700">Bairro</span>
                <input
                  value={form.neighborhood}
                  onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">Referência</span>
              <input
                value={form.address_reference}
                onChange={(e) => setForm({ ...form, address_reference: e.target.value })}
                placeholder="Ex.: em frente ao nº 500, próximo ao poste 42"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">
                Fonte da imagem usada para posicionar
              </span>
              <input
                value={form.location_source}
                onChange={(e) => setForm({ ...form, location_source: e.target.value })}
                placeholder="Ex.: Ortofoto municipal 2025, Google Híbrido"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
              />
              <span className="text-[11px] text-foreground-400 mt-1 block">
                Registrada junto com o exemplar para que a qualidade da coordenada
                possa ser avaliada depois.
              </span>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-foreground-700">Observação</span>
              <textarea
                value={form.field_notes}
                onChange={(e) => setForm({ ...form, field_notes: e.target.value })}
                rows={3}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm resize-none"
              />
            </label>

            <BasePhotoPicker file={photo} onChange={setPhoto} />
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 2 && coords && (
          <>
            <div className="rounded-lg border border-background-200 divide-y divide-background-200">
              {[
                ["Projeto", selectedProject?.name ?? "—"],
                ["Tipo", INDIVIDUAL_TYPES.find((t) => t.value === form.individual_type)?.label ?? "—"],
                [
                  "Espécie",
                  selectedSpecies
                    ? `${selectedSpecies.popularName} — ${selectedSpecies.scientificName}`
                    : "Não identificada",
                ],
                ["Código CTA", form.cta_code.trim() || "Atribuído pelo servidor"],
                ["Coordenada", `${formatCoord(coords.latitude)} , ${formatCoord(coords.longitude)}`],
                [
                  "Como foi posicionado",
                  method === "gps"
                    ? `GPS do aparelho${gpsAccuracy != null ? ` (precisão ${gpsAccuracy.toFixed(1)} m)` : ""}`
                    : method === "map"
                    ? "Clique no mapa/ortofoto"
                    : method === "coordinates"
                      ? "Coordenadas informadas"
                      : `Endereço (${addressPick?.label ?? "busca"}), ajustado no mapa`,
                ],
                ["Foto geral", photo ? photo.name : "Sem foto"],
                ["Fonte da imagem", form.location_source.trim() || "Não informada"],
                [
                  "Campanha",
                  campaigns.find((c) => c.id === form.campaign_id)?.name ?? "Sem campanha",
                ],
                [
                  "Endereço",
                  [form.street, form.neighborhood].filter(Boolean).join(", ") || "Não informado",
                ],
              ].map(([label, value]) => (
                // No celular o rotulo vai acima do valor; a coluna fixa espremia o valor.
                <div key={label} className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-3 px-3 py-2.5">
                  <span className="text-xs text-foreground-500 sm:w-44 sm:shrink-0">{label}</span>
                  <span className="text-sm text-foreground-900 min-w-0 break-words">{value}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-background-50 border border-background-200 px-3 py-2.5">
              <i className="ri-information-line text-foreground-500 mt-0.5" />
              <p className="text-xs text-foreground-500">
                Cadastro base: a posição fica registrada como{" "}
                <strong>não confirmada em campo</strong> até que alguém a valide
                presencialmente. Dendrometria, fitossanidade e risco são
                preenchidos depois, na vistoria.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => (step === 0 ? navigate("/cta") : setStep(step - 1))}
          className="px-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-700 hover:bg-background-50 cursor-pointer"
        >
          {step === 0 ? "Cancelar" : "Voltar"}
        </button>

        {createdTreeId !== null ? (
          <button
            onClick={() => navigate(`/cta/${createdTreeId}`)}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 cursor-pointer"
          >
            Abrir ficha do exemplar
          </button>
        ) : step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={step === 0 ? !canLeaveLocation : !canLeaveIdentification}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:bg-background-300 disabled:cursor-not-allowed cursor-pointer"
          >
            Continuar
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:bg-background-300 disabled:cursor-not-allowed cursor-pointer"
          >
            {saving ? (savingStep ?? "Cadastrando...") : "Cadastrar exemplar"}
          </button>
        )}
      </div>
    </div>
  );
}
